//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Map utilities
//
//  Copyright:    © 2025 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.7.0
//    001   13.06.25 Sean Flook       IMANN-1812 Initial revision.
//    002   15.09.25 Sean Flook       IMANN-1812 Added new constants and functions to handle layer opacities.
//    003   16.09.25 Sean Flook       IMANN-1962 Added new constants and functions to handle layer visibilities.
//    004   23.09.25 Sean Flook       IMANN-2014 Changes required to deal with historical provenance data.
//    005   24.10.25 Sean Flook       IMANN-1129 Added methods used to style shape files when loaded.
//    006   30.10.25 Sean Flook       IMANN-2092 Added getCoordRefSystemName to get the name of the coordinate reference system.
//    007   24.03.26 Sean Flook       IMANN-2369 Added getExtentBuffer to get the buffer for an extent.
//    008   25.03.26 Sean Flook       IMANN-2370 Round buffer to nearest 10 for cleaner values.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    009   28.04.26 Sean Flook       IMANN-2416 Updated image for Add range of properties and Add range of children actions.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header

import {
  GetStreetMapSymbol,
  GetESUMapSymbol,
  GetLlpgStreetMapSymbol,
  GetBackgroundPropertyMapSymbol,
  GetASDMapSymbol,
  GetPropertyMapSymbol,
  GetExtentMapSymbol,
} from "../utils/ADSMapSymbols";

import { adsWhite } from "./../utils/ADSColours";

export const backgroundStreetLayerName = "backgroundStreetLayer";
export const unassignedEsusLayerName = "unassignedEsusLayer";
export const backgroundPropertyLayerName = "backgroundPropertyLayer";
export const backgroundProvenanceLayerName = "backgroundProvenanceLayer";
export const selectPropertyLayerName = "selectPropertyLayer";
export const streetLayerName = "streetLayer";
export const llpgStreetLayerName = "llpgStreetLayer";
export const asd51LayerName = "asd51Layer";
export const asd52LayerName = "asd52Layer";
export const asd53LayerName = "asd53Layer";
export const asd61LayerName = "asd61Layer";
export const asd62LayerName = "asd62Layer";
export const asd63LayerName = "asd63Layer";
export const asd64LayerName = "asd64Layer";
export const asd66LayerName = "asd66Layer";
export const propertyLayerName = "propertyLayer";
export const extentLayerName = "extentLayer";
export const zoomGraphicsLayerName = "zoomGraphicsLayer";
export const labelLayerName = "labelLayer";
export const sketchLayerName = "sketchLayer";

export const streetLayerTitle = "Street layer";
export const llpgStreetLayerTitle = "LLPG Street layer";
export const backgroundStreetLayerTitle = "Background street layer";
export const unassignedEsusLayerTitle = "Unassigned ESUs layer";
export const asd51LayerTitle = "Maintenance responsibility layer";
export const asd52LayerTitle = "Reinstatement category layer";
export const asd53LayerTitle = "Special designation layer";
export const asd61LayerTitle = "Interest layer";
export const asd62LayerTitle = "Construction layer";
export const asd63LayerTitle = "Special designation layer";
export const asd64LayerTitle = "Height, width & weight restriction layer";
export const asd66LayerTitle = "Public right of way layer";
export const propertyLayerTitle = "Property layer";
export const provenanceLayerTitle = "Provenance layer";
export const backgroundPropertyLayerTitle = "Background property layer";
export const backgroundProvenanceLayerTitle = "Background provenance layer";
export const selectPropertyLayerTitle = "Select property layer";
export const sketchLayerTitle = "Edit layer";
export const zoomLayerTitle = "Zoom layer";
export const labelLayerTitle = "Label layer";

export const doubleEventWait = 1500;

const showHistoricalExtentsKey = "showHistoricalExtentData";
const showHistoricalBackgroundProvenanceKey = "showHistoricalBackgroundProvenanceData";

const defaultOpacity = {
  street: 1,
  editStreet: 0.5,
  extent: 1,
  editExtent: 0.5,
  backgroundStreets: 0.4,
  editBackgroundStreets: 0.25,
  unassignedEsus: 0.75,
  editUnassignedEsus: 0.25,
  backgroundExtents: 0.5,
  editBackgroundExtents: 0.25,
  backgroundProperties: 0.5,
  editBackgroundProperties: 0.25,
  property: 1,
  editProperty: 0.5,
};

const streetOpacityKey = "streetOpacity";
const editStreetOpacityKey = "editStreetOpacity";
const unassignedEsusOpacityKey = "unassignedEsusOpacity";
const editUnassignedEsusOpacityKey = "editUnassignedEsusOpacity";
const backgroundStreetOpacityKey = "backgroundStreetOpacity";
const editBackgroundStreetOpacityKey = "editBackgroundStreetOpacity";
const propertyOpacityKey = "propertyOpacity";
const editPropertyOpacityKey = "editPropertyOpacity";
const provenanceOpacityKey = "provenanceOpacity";
const editProvenanceOpacityKey = "editProvenanceOpacity";
const backgroundPropertiesOpacityKey = "backgroundPropertiesOpacity";
const editBackgroundPropertiesOpacityKey = "editBackgroundPropertiesOpacity";
const backgroundProvenanceOpacityKey = "backgroundProvenanceOpacity";
const editBackgroundProvenanceOpacityKey = "editBackgroundProvenanceOpacity";

const defaultVisibility = {
  backgroundStreets: true,
  unassignedEsus: true,
  backgroundProvenances: true,
  backgroundProperties: true,
};

const backgroundStreetVisibilityKey = "backgroundStreetVisibility";
const unassignedEsusVisibilityKey = "unassignedEsusVisibility";
const backgroundProvenanceVisibilityKey = "backgroundProvenanceVisibility";
const backgroundPropertyVisibilityKey = "backgroundPropertyVisibility";

const defaultValidColour = [117, 112, 179]; // purple
const defaultInvalidColour = [255, 0, 0]; // red
const defaultExtraLineWidth = 5;
const defaultEditStyle = "solid";

/** Line symbol for unselected features */
export const unselectedLineSymbol = {
  type: "simple-line",
  color: [51, 136, 255, 0.5],
  width: 1,
};

/** Line symbol for selected features */
export const selectedLineSymbol = {
  type: "simple-line",
  color: [51, 136, 255, 1],
  width: 3,
};

/** Line symbol for valid line features */
export const validLineSymbol = {
  type: "simple-line",
  color: defaultValidColour,
  width: defaultExtraLineWidth,
  style: defaultEditStyle,
};

/** Line symbol for invalid line features */
export const invalidLineSymbol = {
  type: "simple-line",
  color: defaultInvalidColour,
  width: defaultExtraLineWidth,
  style: defaultEditStyle,
};

/** Polygon symbol for valid polygon features */
export const validPolygonSymbol = {
  type: "simple-fill",
  color: defaultValidColour,
  outline: {
    color: defaultValidColour,
    width: defaultExtraLineWidth,
  },
};

/** Polygon symbol for invalid polygon features */
export const invalidPolygonSymbol = {
  type: "simple-fill",
  color: defaultInvalidColour,
  outline: {
    color: defaultInvalidColour,
    width: defaultExtraLineWidth,
  },
};

/** Action to open street record */
export const streetOpenAction = {
  title: "Open street record",
  id: "open-street-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 51 */
export const asd51OpenAction = {
  title: "Open ASD record",
  id: "open-asd-51-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 52 */
export const asd52OpenAction = {
  title: "Open ASD record",
  id: "open-asd-52-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 53 */
export const asd53OpenAction = {
  title: "Open ASD record",
  id: "open-asd-53-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 61 */
export const asd61OpenAction = {
  title: "Open ASD record",
  id: "open-asd-61-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 62 */
export const asd62OpenAction = {
  title: "Open ASD record",
  id: "open-asd-62-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 63 */
export const asd63OpenAction = {
  title: "Open ASD record",
  id: "open-asd-63-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 64 */
export const asd64OpenAction = {
  title: "Open ASD record",
  id: "open-asd-64-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open ASD record 66 */
export const asd66OpenAction = {
  title: "Open ASD record",
  id: "open-asd-66-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to open street in Street View */
export const streetStreetViewAction = {
  title: "Open in Street View",
  id: "street-street-view",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iIzAwMDAwMCI+PHBhdGggZD0iTTAgMGgyNHYyNEgwVjB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTEyLjU2IDE0LjMzYy0uMzQuMjctLjU2LjctLjU2IDEuMTdWMjFoN2MxLjEgMCAyLS45IDItMnYtNS45OGMtLjk0LS4zMy0xLjk1LS41Mi0zLS41Mi0yLjAzIDAtMy45My43LTUuNDQgMS44M3oiLz48Y2lyY2xlIGN4PSIxOCIgY3k9IjYiIHI9IjUiLz48cGF0aCBkPSJNMTEuNSA2YzAtMS4wOC4yNy0yLjEuNzQtM0g1Yy0xLjEgMC0yIC45LTIgMnYxNGMwIC41NS4yMyAxLjA1LjU5IDEuNDFsOS44Mi05LjgyQzEyLjIzIDkuNDIgMTEuNSA3LjggMTEuNSA2eiIvPjwvc3ZnPg==",
};

/** Action to add a property to a street */
export const streetAddProperty = {
  title: "Add property",
  id: "add-property",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iIzAwMDAwMCI+PHBhdGggZD0iTTAgMGgyNHYyNEgwVjB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTEzIDdoLTJ2NEg3djJoNHY0aDJ2LTRoNHYtMmgtNFY3em0tMS01QzYuNDggMiAyIDYuNDggMiAxMnM0LjQ4IDEwIDEwIDEwIDEwLTQuNDggMTAtMTBTMTcuNTIgMiAxMiAyem0wIDE4Yy00LjQxIDAtOC0zLjU5LTgtOHMzLjU5LTggOC04IDggMy41OSA4IDgtMy41OSA4LTggOHoiLz48L3N2Zz4=",
};

/** Action to add multiple properties to a street */
export const streetAddRangeProperties = {
  title: "Add properties",
  id: "add-range-properties",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAtOTYwIDk2MCA5NjAiIHdpZHRoPSIyNHB4IiBmaWxsPSIjMWYxZjFmIj48cGF0aCBkPSJNNTYwLTMyMGg4MHYtMTIwaDEyMHYtODBINjQwdi0xMjBoLTgwdjEyMEg0NDB2ODBoMTIwdjEyMFpNMjQwLTE0MFExMzEtMTc4IDY1LjUtMjcxLjVUMC00ODBxMC0xMTUgNjUuNS0yMDguNVQyNDAtODIwdjg4cS03NCAzNS0xMTcgMTAzVDgwLTQ4MHEwIDgxIDQzIDE0OXQxMTcgMTAzdjg4Wm0yMTkuNS04LjVxLTY1LjUtMjguNS0xMTQtNzd0LTc3LTExNFEyNDAtNDA1IDI0MC00ODB0MjguNS0xNDAuNXEyOC41LTY1LjUgNzctMTE0dDExNC03N1E1MjUtODQwIDYwMC04NDB0MTQwLjUgMjguNXE2NS41IDI4LjUgMTE0IDc3dDc3IDExNFE5NjAtNTU1IDk2MC00ODB0LTI4LjUgMTQwLjVxLTI4LjUgNjUuNS03NyAxMTR0LTExNCA3N1E2NzUtMTIwIDYwMC0xMjB0LTE0MC41LTI4LjVaTTYwMC00ODBabTAgMjgwcTExNyAwIDE5OC41LTgxLjVUODgwLTQ4MHEwLTExNy04MS41LTE5OC41VDYwMC03NjBxLTExNyAwLTE5OC41IDgxLjVUMzIwLTQ4MHEwIDExNyA4MS41IDE5OC41VDYwMC0yMDBaIi8+PC9zdmc+",
};

/** Action to open property record */
export const propertyOpenAction = {
  title: "Open property record",
  id: "open-property-record",
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAcUlEQVR4Ae2VsQ3AIBDEPEE6dkrGy2xsAIOQhtoSOppIWPoObD0NHFZ5gA4MmYmcEZpfDANywChA3RYQec0DLi95wOXkAZfnAZc7HnD5joDLoydyeR64RL5tg9fleWBuYuQBfhNowAinZR+OTwduVjh8GbqRVAucOQEAAAAASUVORK5CYII=",
};

/** Action to add a child property */
export const propertyAddChild = {
  title: "Add child",
  id: "add-child",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iIzAwMDAwMCI+PHBhdGggZD0iTTAgMGgyNHYyNEgwVjB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTEzIDdoLTJ2NEg3djJoNHY0aDJ2LTRoNHYtMmgtNFY3em0tMS01QzYuNDggMiAyIDYuNDggMiAxMnM0LjQ4IDEwIDEwIDEwIDEwLTQuNDggMTAtMTBTMTcuNTIgMiAxMiAyem0wIDE4Yy00LjQxIDAtOC0zLjU5LTgtOHMzLjU5LTggOC04IDggMy41OSA4IDgtMy41OSA4LTggOHoiLz48L3N2Zz4=",
};

/** Action to add multiple child properties */
export const propertyAddRangeChildren = {
  title: "Add children",
  id: "add-range-children",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAtOTYwIDk2MCA5NjAiIHdpZHRoPSIyNHB4IiBmaWxsPSIjMWYxZjFmIj48cGF0aCBkPSJNNTYwLTMyMGg4MHYtMTIwaDEyMHYtODBINjQwdi0xMjBoLTgwdjEyMEg0NDB2ODBoMTIwdjEyMFpNMjQwLTE0MFExMzEtMTc4IDY1LjUtMjcxLjVUMC00ODBxMC0xMTUgNjUuNS0yMDguNVQyNDAtODIwdjg4cS03NCAzNS0xMTcgMTAzVDgwLTQ4MHEwIDgxIDQzIDE0OXQxMTcgMTAzdjg4Wm0yMTkuNS04LjVxLTY1LjUtMjguNS0xMTQtNzd0LTc3LTExNFEyNDAtNDA1IDI0MC00ODB0MjguNS0xNDAuNXEyOC41LTY1LjUgNzctMTE0dDExNC03N1E1MjUtODQwIDYwMC04NDB0MTQwLjUgMjguNXE2NS41IDI4LjUgMTE0IDc3dDc3IDExNFE5NjAtNTU1IDk2MC00ODB0LTI4LjUgMTQwLjVxLTI4LjUgNjUuNS03NyAxMTR0LTExNCA3N1E2NzUtMTIwIDYwMC0xMjB0LTE0MC41LTI4LjVaTTYwMC00ODBabTAgMjgwcTExNyAwIDE5OC41LTgxLjVUODgwLTQ4MHEwLTExNy04MS41LTE5OC41VDYwMC03NjBxLTExNyAwLTE5OC41IDgxLjVUMzIwLTQ4MHEwIDExNyA4MS41IDE5OC41VDYwMC0yMDBaIi8+PC9zdmc+",
};

/** Action to open property in Street View */
export const propertyStreetViewAction = {
  title: "Open in Street View",
  id: "property-street-view",
  image:
    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iIzAwMDAwMCI+PHBhdGggZD0iTTAgMGgyNHYyNEgwVjB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTEyLjU2IDE0LjMzYy0uMzQuMjctLjU2LjctLjU2IDEuMTdWMjFoN2MxLjEgMCAyLS45IDItMnYtNS45OGMtLjk0LS4zMy0xLjk1LS41Mi0zLS41Mi0yLjAzIDAtMy45My43LTUuNDQgMS44M3oiLz48Y2lyY2xlIGN4PSIxOCIgY3k9IjYiIHI9IjUiLz48cGF0aCBkPSJNMTEuNSA2YzAtMS4wOC4yNy0yLjEuNzQtM0g1Yy0xLjEgMC0yIC45LTIgMnYxNGMwIC41NS4yMyAxLjA1LjU5IDEuNDFsOS44Mi05LjgyQzEyLjIzIDkuNDIgMTEuNSA3LjggMTEuNSA2eiIvPjwvc3ZnPg==",
};

/** Default simple point symbol JSON */
export const DEFAULT_SIMPLE_POINT_JSON = {
  type: "esriSMS",
  color: [255, 255, 255, 64],
  angle: 0,
  xoffset: 0,
  yoffset: 0,
  size: 12,
  style: "esriSMSCircle",
  outline: {
    type: "esriSLS",
    color: [0, 122, 194, 255],
    width: 1,
    style: "esriSLSSolid",
  },
};

/** Default simple line symbol JSON */
export const DEFAULT_SIMPLE_LINE_JSON = {
  type: "esriSLS",
  color: [0, 122, 194, 255],
  width: 1,
  style: "esriSLSSolid",
};

/** Default simple polygon symbol JSON */
export const DEFAULT_SIMPLE_POLYGON_JSON = {
  type: "esriSFS",
  color: [0, 122, 194, 0],
  outline: {
    type: "esriSLS",
    color: [0, 122, 194, 255],
    width: 1,
    style: "esriSLSSolid",
  },
  style: "esriSFSSolid",
};

/** Renderer for background street layer */
export const streetRenderer = {
  type: "unique-value",
  field: "SymbolCode",
  uniqueValueInfos: [
    {
      value: "0, 0",
      symbol: GetESUMapSymbol(),
      label: "Unassigned ESU",
    },
    {
      value: "0, 1",
      symbol: GetESUMapSymbol(),
      label: "Unassigned ESU, Under construction",
    },
    {
      value: "0, 2",
      symbol: GetESUMapSymbol(),
      label: "Unassigned ESU, Open",
    },
    {
      value: "0, 4",
      symbol: GetESUMapSymbol(true),
      label: "Unassigned ESU, Permanently closed",
    },
    {
      value: "0, 5",
      symbol: GetESUMapSymbol(),
      label: "Unassigned ESU, Street for addressing purposes only",
    },
    {
      value: "1, 1",
      symbol: GetStreetMapSymbol(),
      label: "Official Designated Street Name, Under construction",
    },
    {
      value: "1, 2",
      symbol: GetStreetMapSymbol(),
      label: "Official Designated Street Name, Open",
    },
    {
      value: "1, 4",
      symbol: GetStreetMapSymbol(true),
      label: "Official Designated Street Name, Permanently closed",
    },
    {
      value: "1, 5",
      symbol: GetStreetMapSymbol(),
      label: "Official Designated Street Name, Street for addressing purposes only",
    },
    {
      value: "2, 1",
      symbol: GetStreetMapSymbol(),
      label: "Street Description, Under construction",
    },
    {
      value: "2, 2",
      symbol: GetStreetMapSymbol(),
      label: "Street Description, Open",
    },
    {
      value: "2, 4",
      symbol: GetStreetMapSymbol(true),
      label: "Street Description, Permanently closed",
    },
    {
      value: "2, 5",
      symbol: GetStreetMapSymbol(),
      label: "Street Description, Street for addressing purposes only",
    },
    {
      value: "3, 1",
      symbol: GetStreetMapSymbol(),
      label: "Numbered Street, Under construction",
    },
    {
      value: "3, 2",
      symbol: GetStreetMapSymbol(),
      label: "Numbered Street, Open",
    },
    {
      value: "3, 4",
      symbol: GetStreetMapSymbol(true),
      label: "Numbered Street, Permanently closed",
    },
    {
      value: "3, 5",
      symbol: GetStreetMapSymbol(),
      label: "Numbered Street, Street for addressing purposes only",
    },
    {
      value: "4, 1",
      symbol: GetStreetMapSymbol(),
      label: "Unofficial Street Description, Under construction",
    },
    {
      value: "4, 2",
      symbol: GetStreetMapSymbol(),
      label: "Unofficial Street Description, Open",
    },
    {
      value: "4, 4",
      symbol: GetStreetMapSymbol(true),
      label: "Unofficial Street Description, Permanently closed",
    },
    {
      value: "4, 5",
      symbol: GetStreetMapSymbol(),
      label: "Unofficial Street Description, Street for addressing purposes only",
    },
    {
      value: "9, 1",
      symbol: GetStreetMapSymbol(),
      label: "Description used for LLPG Access, Under construction",
    },
    {
      value: "9, 2",
      symbol: GetStreetMapSymbol(),
      label: "Description used for LLPG Access, Open",
    },
    {
      value: "9, 4",
      symbol: GetStreetMapSymbol(true),
      label: "Description used for LLPG Access, Permanently closed",
    },
    {
      value: "9, 5",
      symbol: GetStreetMapSymbol(),
      label: "Description used for LLPG Access, Street for addressing purposes only",
    },
    {
      value: "-1, 1, 1",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Official Designated Street Name, Under construction",
    },
    {
      value: "-1, 1, 2",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Official Designated Street Name, Open",
    },
    {
      value: "-1, 1, 4",
      symbol: GetLlpgStreetMapSymbol(true),
      label: "Official Designated Street Name, Permanently closed",
    },
    {
      value: "-1, 1, 5",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Official Designated Street Name, Street for addressing purposes only",
    },
    {
      value: "-1, 2, 1",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Street Description, Under construction",
    },
    {
      value: "-1, 2, 2",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Street Description, Open",
    },
    {
      value: "-1, 2, 4",
      symbol: GetLlpgStreetMapSymbol(true),
      label: "Street Description, Permanently closed",
    },
    {
      value: "-1, 2, 5",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Street Description, Street for addressing purposes only",
    },
    {
      value: "-1, 3, 1",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Numbered Street, Under construction",
    },
    {
      value: "-1, 3, 2",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Numbered Street, Open",
    },
    {
      value: "-1, 3, 4",
      symbol: GetLlpgStreetMapSymbol(true),
      label: "Numbered Street, Permanently closed",
    },
    {
      value: "-1, 3, 5",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Numbered Street, Street for addressing purposes only",
    },
    {
      value: "-1, 4, 1",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Unofficial Street Description, Under construction",
    },
    {
      value: "-1, 4, 2",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Unofficial Street Description, Open",
    },
    {
      value: "-1, 4, 4",
      symbol: GetLlpgStreetMapSymbol(true),
      label: "Unofficial Street Description, Permanently closed",
    },
    {
      value: "-1, 4, 5",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Unofficial Street Description, Street for addressing purposes only",
    },
    {
      value: "-1, 9, 1",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Description used for LLPG Access, Under construction",
    },
    {
      value: "-1, 9, 2",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Description used for LLPG Access, Open",
    },
    {
      value: "-1, 9, 4",
      symbol: GetLlpgStreetMapSymbol(true),
      label: "Description used for LLPG Access, Permanently closed",
    },
    {
      value: "-1, 9, 5",
      symbol: GetLlpgStreetMapSymbol(),
      label: "Description used for LLPG Access, Street for addressing purposes only",
    },
  ],
};

/** Renderers for ASD 51 layers */
export const asd51Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(51),
};

/** Renderers for ASD 52 layers */
export const asd52Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(52),
};

/** Renderers for ASD 53 layers */
export const asd53Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(53),
};

/** Renderers for ASD 61 layers */
export const asd61Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(61),
};

/** Renderers for ASD 62 layers */
export const asd62Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(62),
};

/** Renderers for ASD 63 layers */
export const asd63Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(63),
};

/** Renderers for ASD 64 layers */
export const asd64Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(64),
};

/** Renderers for ASD 66 layers */
export const asd66Renderer = {
  type: "simple",
  symbol: GetASDMapSymbol(66),
};

/** Renderer for background property layer */
export const backgroundPropertyRenderer = {
  type: "unique-value",
  field: "DisplayLogicalStatus",
  uniqueValueInfos: [
    {
      value: "1",
      symbol: GetBackgroundPropertyMapSymbol(1),
      label: "Approved Preferred",
    },
    {
      value: "3",
      symbol: GetBackgroundPropertyMapSymbol(3),
      label: "Alternative",
    },
    {
      value: "5",
      symbol: GetBackgroundPropertyMapSymbol(5),
      label: "Candidate",
    },
    {
      value: "6",
      symbol: GetBackgroundPropertyMapSymbol(6),
      label: "Provisional",
    },
    {
      value: "7",
      symbol: GetBackgroundPropertyMapSymbol(7),
      label: "Rejected",
    },
    {
      value: "8",
      symbol: GetBackgroundPropertyMapSymbol(8),
      label: "Historical",
    },
    {
      value: "9",
      symbol: GetBackgroundPropertyMapSymbol(9),
      label: "Rejected",
    },
  ],
};

/** Renderer for foreground property layer */
export const propertyRenderer = {
  type: "unique-value",
  field: "SymbolCode",
  uniqueValueInfos: [
    {
      value: "1, B",
      symbol: GetPropertyMapSymbol(1, "B"),
      label: "Approved Preferred, Street BLPU",
    },
    {
      value: "1, C",
      symbol: GetPropertyMapSymbol(1, "C"),
      label: "Approved Preferred, Commercial",
    },
    {
      value: "1, L",
      symbol: GetPropertyMapSymbol(1, "L"),
      label: "Approved Preferred, Land",
    },
    {
      value: "1, M",
      symbol: GetPropertyMapSymbol(1, "M"),
      label: "Approved Preferred, Military",
    },
    {
      value: "1, P",
      symbol: GetPropertyMapSymbol(1, "P"),
      label: "Approved Preferred, Parent Shell",
    },
    {
      value: "1, R",
      symbol: GetPropertyMapSymbol(1, "R"),
      label: "Approved Preferred, Residential",
    },
    {
      value: "1, U",
      symbol: GetPropertyMapSymbol(1, "U"),
      label: "Approved Preferred, Unclassified",
    },
    {
      value: "1, X",
      symbol: GetPropertyMapSymbol(1, "X"),
      label: "Approved Preferred, Dual Use",
    },
    {
      value: "1, Z",
      symbol: GetPropertyMapSymbol(1, "Z"),
      label: "Approved Preferred, Object of Interest",
    },
    {
      value: "3, B",
      symbol: GetPropertyMapSymbol(3, "B"),
      label: "Alternative, Street BLPU",
    },
    {
      value: "3, C",
      symbol: GetPropertyMapSymbol(3, "C"),
      label: "Alternative, Commercial",
    },
    {
      value: "3, L",
      symbol: GetPropertyMapSymbol(3, "L"),
      label: "Alternative, Land",
    },
    {
      value: "3, M",
      symbol: GetPropertyMapSymbol(3, "M"),
      label: "Alternative, Military",
    },
    {
      value: "3, P",
      symbol: GetPropertyMapSymbol(3, "P"),
      label: "Alternative, Property Shell",
    },
    {
      value: "3, R",
      symbol: GetPropertyMapSymbol(3, "R"),
      label: "Alternative, Residential",
    },
    {
      value: "3, U",
      symbol: GetPropertyMapSymbol(3, "U"),
      label: "Alternative, Unclassified",
    },
    {
      value: "3, X",
      symbol: GetPropertyMapSymbol(3, "X"),
      label: "Alternative, Dual Use",
    },
    {
      value: "3, Z",
      symbol: GetPropertyMapSymbol(3, "Z"),
      label: "Alternative, Object of Interest",
    },
    {
      value: "5, B",
      symbol: GetPropertyMapSymbol(5, "B"),
      label: "Candidate, Street BLPU",
    },
    {
      value: "5, C",
      symbol: GetPropertyMapSymbol(5, "C"),
      label: "Candidate, Commercial",
    },
    {
      value: "5, L",
      symbol: GetPropertyMapSymbol(5, "L"),
      label: "Candidate, Land",
    },
    {
      value: "5, M",
      symbol: GetPropertyMapSymbol(5, "M"),
      label: "Candidate, Military",
    },
    {
      value: "5, P",
      symbol: GetPropertyMapSymbol(5, "P"),
      label: "Candidate, Property Shell",
    },
    {
      value: "5, R",
      symbol: GetPropertyMapSymbol(5, "R"),
      label: "Candidate, Residential",
    },
    {
      value: "5, U",
      symbol: GetPropertyMapSymbol(5, "U"),
      label: "Candidate, Unclassified",
    },
    {
      value: "5, X",
      symbol: GetPropertyMapSymbol(5, "X"),
      label: "Candidate, Dual Use",
    },
    {
      value: "5, Z",
      symbol: GetPropertyMapSymbol(5, "Z"),
      label: "Candidate, Object of Interest",
    },
    {
      value: "6, B",
      symbol: GetPropertyMapSymbol(6, "B"),
      label: "Provisional, Street BLPU",
    },
    {
      value: "6, C",
      symbol: GetPropertyMapSymbol(6, "C"),
      label: "Provisional, Commercial",
    },
    {
      value: "6, L",
      symbol: GetPropertyMapSymbol(6, "L"),
      label: "Provisional, Land",
    },
    {
      value: "6, M",
      symbol: GetPropertyMapSymbol(6, "M"),
      label: "Provisional, Military",
    },
    {
      value: "6, P",
      symbol: GetPropertyMapSymbol(6, "P"),
      label: "Provisional, Property Shell",
    },
    {
      value: "6, R",
      symbol: GetPropertyMapSymbol(6, "R"),
      label: "Provisional, Residential",
    },
    {
      value: "6, U",
      symbol: GetPropertyMapSymbol(6, "U"),
      label: "Provisional, Unclassified",
    },
    {
      value: "6, X",
      symbol: GetPropertyMapSymbol(6, "X"),
      label: "Provisional, Dual Use",
    },
    {
      value: "6, Z",
      symbol: GetPropertyMapSymbol(6, "Z"),
      label: "Provisional, Object of Interest",
    },
    {
      value: "7, B",
      symbol: GetPropertyMapSymbol(7, "B"),
      label: "Rejected (External), Street BLPU",
    },
    {
      value: "7, C",
      symbol: GetPropertyMapSymbol(7, "C"),
      label: "Rejected (External), Commercial",
    },
    {
      value: "7, L",
      symbol: GetPropertyMapSymbol(7, "L"),
      label: "Rejected (External), Land",
    },
    {
      value: "7, M",
      symbol: GetPropertyMapSymbol(7, "M"),
      label: "Rejected (External), Military",
    },
    {
      value: "7, P",
      symbol: GetPropertyMapSymbol(7, "P"),
      label: "Rejected (External), Property Shell",
    },
    {
      value: "7, R",
      symbol: GetPropertyMapSymbol(7, "R"),
      label: "Rejected (External), Residential",
    },
    {
      value: "7, U",
      symbol: GetPropertyMapSymbol(7, "U"),
      label: "Rejected (External), Unclassified",
    },
    {
      value: "7, X",
      symbol: GetPropertyMapSymbol(7, "X"),
      label: "Rejected (External), Dual Use",
    },
    {
      value: "7, Z",
      symbol: GetPropertyMapSymbol(7, "Z"),
      label: "Rejected (External), Object of Interest",
    },
    {
      value: "8, B",
      symbol: GetPropertyMapSymbol(8, "B"),
      label: "Historical, Street BLPU",
    },
    {
      value: "8, C",
      symbol: GetPropertyMapSymbol(8, "C"),
      label: "Historical, Commercial",
    },
    {
      value: "8, L",
      symbol: GetPropertyMapSymbol(8, "L"),
      label: "Historical, Land",
    },
    {
      value: "8, M",
      symbol: GetPropertyMapSymbol(8, "M"),
      label: "Historical, Military",
    },
    {
      value: "8, P",
      symbol: GetPropertyMapSymbol(8, "P"),
      label: "Historical, Property Shell",
    },
    {
      value: "8, R",
      symbol: GetPropertyMapSymbol(8, "R"),
      label: "Historical, Residential",
    },
    {
      value: "8, U",
      symbol: GetPropertyMapSymbol(8, "U"),
      label: "Historical, Unclassified",
    },
    {
      value: "8, X",
      symbol: GetPropertyMapSymbol(8, "X"),
      label: "Historical, Dual Use",
    },
    {
      value: "8, Z",
      symbol: GetPropertyMapSymbol(8, "Z"),
      label: "Historical, Object of Interest",
    },
    {
      value: "9, B",
      symbol: GetPropertyMapSymbol(9, "B"),
      label: "Rejected (Internal), Street BLPU",
    },
    {
      value: "9, C",
      symbol: GetPropertyMapSymbol(9, "C"),
      label: "Rejected (Internal), Commercial",
    },
    {
      value: "9, L",
      symbol: GetPropertyMapSymbol(9, "L"),
      label: "Rejected (Internal), Land",
    },
    {
      value: "9, M",
      symbol: GetPropertyMapSymbol(9, "M"),
      label: "Rejected (Internal), Military",
    },
    {
      value: "9, P",
      symbol: GetPropertyMapSymbol(9, "P"),
      label: "Rejected (Internal), Property Shell",
    },
    {
      value: "9, R",
      symbol: GetPropertyMapSymbol(9, "R"),
      label: "Rejected (Internal), Residential",
    },
    {
      value: "9, U",
      symbol: GetPropertyMapSymbol(9, "U"),
      label: "Rejected (Internal), Unclassified",
    },
    {
      value: "9, X",
      symbol: GetPropertyMapSymbol(9, "X"),
      label: "Rejected (Internal), Dual Use",
    },
    {
      value: "9, Z",
      symbol: GetPropertyMapSymbol(9, "Z"),
      label: "Rejected (Internal), Object of Interest",
    },
  ],
};

/** Renderer for provenance layer */
export const extentRenderer = {
  type: "unique-value",
  field: "SymbolCode",
  uniqueValueInfos: [
    {
      value: "0, F",
      symbol: GetExtentMapSymbol(false, "F"),
      label: "Formal tenancy agreement.",
    },
    {
      value: "0, L",
      symbol: GetExtentMapSymbol(false, "L"),
      label: "Unregistered land title.",
    },
    {
      value: "0, O",
      symbol: GetExtentMapSymbol(false, "O"),
      label: "Occupancy.",
    },
    {
      value: "0, P",
      symbol: GetExtentMapSymbol(false, "P"),
      label: "Inferred from physical features.",
    },
    {
      value: "0, R",
      symbol: GetExtentMapSymbol(false, "R"),
      label: "Rental agreement.",
    },
    {
      value: "0, T",
      symbol: GetExtentMapSymbol(false, "T"),
      label: "Registered title.",
    },
    {
      value: "0, U",
      symbol: GetExtentMapSymbol(false, "U"),
      label: "Inferred from use.",
    },
    {
      value: "1, F",
      symbol: GetExtentMapSymbol(true, "F"),
      label: "Formal tenancy agreement.",
    },
    {
      value: "1, L",
      symbol: GetExtentMapSymbol(true, "L"),
      label: "Unregistered land title.",
    },
    {
      value: "1, O",
      symbol: GetExtentMapSymbol(true, "O"),
      label: "Occupancy.",
    },
    {
      value: "1, P",
      symbol: GetExtentMapSymbol(true, "P"),
      label: "Inferred from physical features.",
    },
    {
      value: "1, R",
      symbol: GetExtentMapSymbol(true, "R"),
      label: "Rental agreement.",
    },
    {
      value: "1, T",
      symbol: GetExtentMapSymbol(true, "T"),
      label: "Registered title.",
    },
    {
      value: "1, U",
      symbol: GetExtentMapSymbol(true, "U"),
      label: "Inferred from use.",
    },
  ],
};

/**
 * The feature reduction used for clustering properties.
 *
 * @param {Number} radius The cluster radius
 * @param {Number} minSize The minimum cluster size
 * @param {Number} maxSize The maximum cluster size
 * @param {Number} yOffset The vertical offset for labels
 * @return {object} Feature reduction JSON
 */
export function PropertyFeatureReduction(radius, minSize, maxSize, yOffset) {
  return {
    type: "cluster",
    clusterRadius: radius,
    clusterMinSize: minSize, //15,
    clusterMaxSize: maxSize, //15,
    labelingInfo: [
      {
        labelExpressionInfo: {
          expression: "Text($feature.cluster_count, '#,###')",
        },
        deconflictionStrategy: "none",
        labelPlacement: "center-center",
        symbol: {
          type: "text",
          color: adsWhite,
          font: {
            weight: "bold",
            size: "10px",
          },
          yoffset: yOffset,
          haloSize: 0.5,
          haloColor: "black",
        },
      },
    ],
    popupTemplate: {
      title: "Multiple addresses",
      content: "This cluster represents <b>{cluster_count}</b> addresses.",
      fieldInfos: [
        {
          fieldName: "cluster_count",
          format: {
            digitSeparator: true,
            places: 0,
          },
        },
      ],
    },
  };
}

/**
 * Get the opacity for a specific layer.
 *
 * @param {string} layerTitle - The layer title.
 * @param {boolean} editing - Whether the map is in editing mode.
 * @returns {number} - The opacity value for the layer.
 */
export const getLayerOpacity = (layerTitle, editing) => {
  switch (layerTitle) {
    case streetLayerTitle:
    case llpgStreetLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editStreetOpacityKey)) || defaultOpacity.editStreet
        : JSON.parse(localStorage.getItem(streetOpacityKey)) || defaultOpacity.street;
    case provenanceLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editProvenanceOpacityKey)) || defaultOpacity.editExtent
        : JSON.parse(localStorage.getItem(provenanceOpacityKey)) || defaultOpacity.extent;
    case backgroundStreetLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editBackgroundStreetOpacityKey)) || defaultOpacity.editBackgroundStreets
        : JSON.parse(localStorage.getItem(backgroundStreetOpacityKey)) || defaultOpacity.backgroundStreets;
    case unassignedEsusLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editUnassignedEsusOpacityKey)) || defaultOpacity.editUnassignedEsus
        : JSON.parse(localStorage.getItem(unassignedEsusOpacityKey)) || defaultOpacity.unassignedEsus;
    case backgroundProvenanceLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editBackgroundProvenanceOpacityKey)) || defaultOpacity.editBackgroundExtents
        : JSON.parse(localStorage.getItem(backgroundProvenanceOpacityKey)) || defaultOpacity.backgroundExtents;
    case backgroundPropertyLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editBackgroundPropertiesOpacityKey)) ||
            defaultOpacity.editBackgroundProperties
        : JSON.parse(localStorage.getItem(backgroundPropertiesOpacityKey)) || defaultOpacity.backgroundProperties;
    case propertyLayerTitle:
      return editing
        ? JSON.parse(localStorage.getItem(editPropertyOpacityKey)) || defaultOpacity.editProperty
        : JSON.parse(localStorage.getItem(propertyOpacityKey)) || defaultOpacity.property;
    default:
      return 1;
  }
};

/**
 * Set the opacity for a specific layer.
 *
 * @param {string} layerTitle - The layer title.
 * @param {boolean} editing - Whether the map is in editing mode.
 * @param {number} opacity - The opacity value to set.
 */
export const setLayerOpacity = (layerTitle, editing, opacity) => {
  switch (layerTitle) {
    case streetLayerTitle:
    case llpgStreetLayerTitle:
      if (editing) {
        localStorage.setItem(editStreetOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(streetOpacityKey, JSON.stringify(opacity));
      }
      break;
    case provenanceLayerTitle:
      if (editing) {
        localStorage.setItem(editProvenanceOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(provenanceOpacityKey, JSON.stringify(opacity));
      }
      break;
    case backgroundStreetLayerTitle:
      if (editing) {
        localStorage.setItem(editBackgroundStreetOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(backgroundStreetOpacityKey, JSON.stringify(opacity));
      }
      break;
    case unassignedEsusLayerTitle:
      if (editing) {
        localStorage.setItem(editUnassignedEsusOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(unassignedEsusOpacityKey, JSON.stringify(opacity));
      }
      break;
    case backgroundProvenanceLayerTitle:
      if (editing) {
        localStorage.setItem(editBackgroundProvenanceOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(backgroundProvenanceOpacityKey, JSON.stringify(opacity));
      }
      break;
    case backgroundPropertyLayerTitle:
      if (editing) {
        localStorage.setItem(editBackgroundPropertiesOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(backgroundPropertiesOpacityKey, JSON.stringify(opacity));
      }
      break;
    case propertyLayerTitle:
      if (editing) {
        localStorage.setItem(editPropertyOpacityKey, JSON.stringify(opacity));
      } else {
        localStorage.setItem(propertyOpacityKey, JSON.stringify(opacity));
      }
      break;
    default:
      break;
  }
};

/**
 * Get the visibility for a specific layer.
 *
 * @param {string} layerTitle - The layer title.
 * @returns {boolean} - The current visibility state of the layer.
 */
export const getLayerVisibility = (layerTitle) => {
  let visibility = "";
  switch (layerTitle) {
    case backgroundStreetLayerTitle:
      visibility = localStorage.getItem(backgroundStreetVisibilityKey);
      return visibility !== null ? visibility === "true" : defaultVisibility.backgroundStreets;
    case unassignedEsusLayerTitle:
      visibility = localStorage.getItem(unassignedEsusVisibilityKey);
      return visibility !== null ? visibility === "true" : defaultVisibility.unassignedEsus;
    case backgroundProvenanceLayerTitle:
      visibility = localStorage.getItem(backgroundProvenanceVisibilityKey);
      return visibility !== null ? visibility === "true" : defaultVisibility.backgroundProvenances;
    case backgroundPropertyLayerTitle:
      visibility = localStorage.getItem(backgroundPropertyVisibilityKey);
      return visibility !== null ? visibility === "true" : defaultVisibility.backgroundProperties;
    default:
      break;
  }
};

/**
 * Set the visibility for a specific layer.
 *
 * @param {string} layerTitle - The layer title.
 * @param {boolean} visibility - The visibility state to set.
 */
export const setLayerVisibility = (layerTitle, visibility) => {
  switch (layerTitle) {
    case backgroundStreetLayerTitle:
      localStorage.setItem(backgroundStreetVisibilityKey, JSON.stringify(visibility));
      break;
    case unassignedEsusLayerTitle:
      localStorage.setItem(unassignedEsusVisibilityKey, JSON.stringify(visibility));
      break;
    case backgroundProvenanceLayerTitle:
      localStorage.setItem(backgroundProvenanceVisibilityKey, JSON.stringify(visibility));
      break;
    case backgroundPropertyLayerTitle:
      localStorage.setItem(backgroundPropertyVisibilityKey, JSON.stringify(visibility));
      break;
    default:
      break;
  }
};

/**
 * Set the visibility of historical data for a specific layer.
 *
 * @param {string} layerTitle - The title of the layer.
 * @param {boolean} show - Whether to show or hide the historical data.
 */
export const setShowHistorical = (layerTitle, show) => {
  switch (layerTitle) {
    case backgroundProvenanceLayerTitle:
      localStorage.setItem(showHistoricalBackgroundProvenanceKey, JSON.stringify(show));
      break;
    case provenanceLayerTitle:
      localStorage.setItem(showHistoricalExtentsKey, JSON.stringify(show));
      break;
    default:
      break;
  }
};

/**
 * Get the visibility of historical data for a specific layer.
 *
 * @param {string} layerTitle - The title of the layer.
 * @returns {boolean} - The current historical data visibility state of the layer.
 */
export const getShowHistorical = (layerTitle) => {
  let showHistorical = "";
  switch (layerTitle) {
    case backgroundProvenanceLayerTitle:
      showHistorical = localStorage.getItem(showHistoricalBackgroundProvenanceKey);
      return showHistorical !== null ? showHistorical === "true" : true;
    case provenanceLayerTitle:
      showHistorical = localStorage.getItem(showHistoricalExtentsKey);
      return showHistorical !== null ? showHistorical === "true" : true;
    default:
      break;
  }
};

/**
 * Fix the symbol JSON in the renderer JSON.
 *
 * @param {object} rendererJson  The renderer JSON we want to fix the symbol JSON for.
 * @returns {object} The renderer JSON with fixed symbol JSON values.
 */
export const fixSymbolJson = (rendererJson) => {
  // Function to convert HSV to RGB
  function hsv2rgb(h, s, v) {
    let f = (n, k = (n + h / 60) % 6) => v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
    return [Math.round(f(5) * 255), Math.round(f(3) * 255), Math.round(f(1) * 255)];
  }

  let rendererString = JSON.stringify(rendererJson);

  while (rendererString.includes('{"type":"CIMHSVColor","values":')) {
    const startIndex = rendererString.indexOf('{"type":"CIMHSVColor","values":') + 32;
    const endIndex = rendererString.indexOf("]}", startIndex);
    const colorValuesString = rendererString.substring(startIndex, endIndex);
    const colorValues = colorValuesString.split(",").map(Number);
    const h = colorValues[0];
    const s = colorValues[1];
    const v = colorValues[2];
    const a = colorValues[3];
    const [newR, newG, newB] = hsv2rgb(h / 360, s / 100, v / 100);
    const beforeColourString = rendererString.substring(0, rendererString.indexOf('{"type":"CIMHSVColor","values":'));
    const afterColourString = rendererString.substring(endIndex + 2);
    rendererString = `${beforeColourString}[${newR},${newG},${newB},${a}]${afterColourString}`;
  }

  while (rendererString.includes('{"type":"CIMRGBColor","values":')) {
    const startIndex = rendererString.indexOf('{"type":"CIMRGBColor","values":') + 32;
    const endIndex = rendererString.indexOf("]}", startIndex);
    const colorValuesString = rendererString.substring(startIndex, endIndex);
    const beforeColourString = rendererString.substring(0, rendererString.indexOf('{"type":"CIMRGBColor","values":'));
    const afterColourString = rendererString.substring(endIndex + 2);
    rendererString = `${beforeColourString}[${colorValuesString}]${afterColourString}`;
  }

  while (rendererString.includes('"height3D"')) {
    const startIndex = rendererString.indexOf('"height3D"') + 32;
    const endIndex = rendererString.indexOf(",", startIndex);
    const before3DString = rendererString.substring(0, rendererString.indexOf('"height3D"'));
    const after3DString = rendererString.substring(endIndex + 1);
    rendererString = `${before3DString}${after3DString}`;
  }

  while (rendererString.includes('"anchor3D"')) {
    const startIndex = rendererString.indexOf('"anchor3D"') + 32;
    const endIndex = rendererString.indexOf(",", startIndex);
    const before3DString = rendererString.substring(0, rendererString.indexOf('"anchor3D"'));
    const after3DString = rendererString.substring(endIndex + 1);
    rendererString = `${before3DString}${after3DString}`;
  }

  return JSON.parse(rendererString.replaceAll(',"angleAlignment":"Map"', ""));
};

/**
 * Get unique value information from the renderer JSON.
 *
 * @param {object} rendererJson  The renderer JSON we want to fix the colour for.
 * @returns {object} The renderer JSON modified for unique value rendering.
 */
export const getUniqueValuesFromRendererJson = (rendererJson) => {
  if (rendererJson.type === "CIMUniqueValueRenderer" && rendererJson.groups && rendererJson.groups.length > 0) {
    return {
      type: "uniqueValue",
      defaultLabel: rendererJson.defaultLabel,
      defaultSymbol: rendererJson.defaultSymbol,
      field1: rendererJson.fields[0],
      uniqueValueGroups: rendererJson.groups.map((group) => {
        return {
          classes: group.classes.map((classInfo) => {
            return {
              label: classInfo.label,
              symbol: classInfo.symbol,
              values: classInfo.values.map((valueInfo) => valueInfo.fieldValues),
            };
          }),
        };
      }),
      uniqueValueInfos: rendererJson.groups[0].classes.map((classInfo) => {
        return {
          value: classInfo.values[0].fieldValues[0],
          symbol: classInfo.symbol,
          label: classInfo.label,
        };
      }),
    };
  } else return rendererJson;
};

/** Get the coordinate reference system name for a given WKID.
 *
 * @param {number} wkid - The well-known ID of the coordinate reference system.
 * @returns {string} - The name of the coordinate reference system.
 */
export const getCoordRefSystemName = async (wkid) => {
  const coordRefSystemNames = await fetch("/coordRefSystemNames.json").then((res) => res.json());

  const coordRefSystem = coordRefSystemNames.find((crs) => crs.wkid === wkid);

  if (coordRefSystem && coordRefSystem.label) {
    return coordRefSystem.label;
  } else {
    return `WKID: ${wkid}`;
  }
};

/**
 * Method to calculate the buffer for an extent based on its size. This is used to determine how much to extend the extent when querying for features around it, ensuring that the buffer is appropriate for the size of the extent.
 *
 * @param {object} extent The extent to get the buffer for
 * @returns {number} The buffer to be used to extend the extent
 */
export const getExtentBuffer = (extent) => {
  const longestSide = Math.max(extent.xmax - extent.xmin, extent.ymax - extent.ymin);
  let newBuffer = 20;
  if (longestSide === 0) {
    return 20; // Default buffer for point extents
  } else if (longestSide > 0 && longestSide < 10000) {
    newBuffer = Math.round(longestSide * 0.1); // Buffer is 10% of the longest side of the extent
  } else {
    newBuffer = Math.round(longestSide * 0.2); // Maximum buffer for very large extents
  }

  if (newBuffer < 20) {
    return 20; // Minimum buffer to ensure some area around the extent is always included
  } else {
    newBuffer = Math.round(newBuffer / 10) * 10; // Round buffer to nearest 10 for cleaner values
    return newBuffer;
  }
};
